import { bindTelegramViewportSync } from './viewport';

const TOKEN_KEY = 'cozzysim_access_token';
const REFRESH_TOKEN_KEY = 'cozzysim_refresh_token';
/** Survives refresh inside the same tab — Telegram Web clears launch hash/initData. */
const TG_SESSION_KEY = 'cozzysim_tg_miniapp';
/** Raw initData captured in index.html before Telegram strips the launch hash. */
const TG_INITDATA_KEY = 'cozzysim_tg_initdata';
const TG_SCRIPT_SRC = 'https://telegram.org/js/telegram-web-app.js';

export type AppMode = 'telegram' | 'web';

function readTelegramSessionFlag(): boolean {
  try {
    return sessionStorage.getItem(TG_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** Remember that this tab is a Telegram Mini App so refresh still loads the SDK. */
export function markTelegramSession(): void {
  try {
    sessionStorage.setItem(TG_SESSION_KEY, '1');
  } catch {
    /* private mode */
  }
}

/** True when we are likely inside Telegram and should load the WebApp SDK. */
export function shouldLoadTelegramSdk(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.Telegram?.WebApp) return true;
  if (readTelegramSessionFlag()) return true;
  if (readStoredInitData()) return true;
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_INIT_DATA) return true;
  const ua = navigator.userAgent || '';
  if (/Telegram/i.test(ua)) return true;
  const hash = window.location.hash || '';
  if (hash.includes('tgWebApp')) return true;
  try {
    if (new URLSearchParams(window.location.search).has('tgWebAppStartParam')) return true;
  } catch {
    /* ignore */
  }
  return false;
}

let telegramScriptPromise: Promise<void> | null = null;

/** Lazily inject Telegram WebApp SDK only when needed (keeps it off the public web critical path). */
export function loadTelegramScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Telegram?.WebApp) return Promise.resolve();
  if (telegramScriptPromise) return telegramScriptPromise;

  telegramScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TG_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Telegram SDK failed to load')), { once: true });
      if (window.Telegram?.WebApp) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = TG_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Telegram SDK failed to load'));
    document.head.appendChild(script);
  });

  return telegramScriptPromise;
}

export function detectAppMode(): AppMode {
  if (typeof window === 'undefined') return 'web';

  const tg = window.Telegram?.WebApp;
  if (tg?.initData) {
    markTelegramSession();
    return 'telegram';
  }

  // Telegram Web (weba/webk) often clears launch hash on refresh — initData is
  // empty but we are still inside the Mini App WebView. Treat that as telegram
  // so we never fall through to the public /login flow.
  if (tg) {
    const platform = (tg.platform || '').toLowerCase();
    if (
      platform === 'weba' ||
      platform === 'web' ||
      platform === 'webk' ||
      platform === 'tdesktop' ||
      platform === 'macos' ||
      platform === 'ios' ||
      platform === 'android' ||
      /Telegram/i.test(navigator.userAgent || '') ||
      readTelegramSessionFlag()
    ) {
      markTelegramSession();
      return 'telegram';
    }
  }

  // Same-tab refresh after a prior Mini App open (SDK may still be loading).
  if (readTelegramSessionFlag()) {
    return 'telegram';
  }

  // Dev: force telegram auth when VITE_DEV_INIT_DATA is set
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_INIT_DATA) {
    return 'telegram';
  }

  return 'web';
}

export function isTelegramWebApp(): boolean {
  return detectAppMode() === 'telegram';
}

export type DevicePlatform = 'ios' | 'android' | 'other';

/**
 * Which mobile wallet to offer (Apple Pay vs Google Pay) — never both. Prefers Telegram's
 * own `WebApp.platform` (reliable, no UA sniffing needed) and falls back to user-agent
 * detection for plain web-mode sessions with no Telegram context.
 */
export function detectDevicePlatform(): DevicePlatform {
  if (typeof window === 'undefined') return 'other';

  const tgPlatform = window.Telegram?.WebApp?.platform;
  if (tgPlatform === 'ios') return 'ios';
  if (tgPlatform === 'android') return 'android';

  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // iPadOS 13+ reports itself as "Macintosh" but is touch-capable, unlike a real Mac.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(ua)) return 'android';

  return 'other';
}

function syncTelegramSafeAreaInsets(): void {
  if (typeof document === 'undefined') return;

  const tg = window.Telegram?.WebApp;
  if (!tg) return;

  const root = document.documentElement;
  const safe = tg.safeAreaInset;
  const content = tg.contentSafeAreaInset;

  if (safe) {
    if (typeof safe.top === 'number') {
      root.style.setProperty('--tg-safe-area-inset-top', `${safe.top}px`);
    }
    if (typeof safe.bottom === 'number') {
      root.style.setProperty('--tg-safe-area-inset-bottom', `${safe.bottom}px`);
    }
  }
  if (content) {
    if (typeof content.top === 'number') {
      root.style.setProperty('--tg-content-safe-area-inset-top', `${content.top}px`);
    }
    if (typeof content.bottom === 'number') {
      root.style.setProperty('--tg-content-safe-area-inset-bottom', `${content.bottom}px`);
    }
  }
}

/** Maximize Mini App viewport; safe for older Telegram clients and SSR. */
export function initTelegramWebApp(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const tg = window.Telegram?.WebApp;
  if (!tg) return;

  markTelegramSession();
  if (tg.initData) persistInitData(tg.initData);

  tg.ready();
  // expand() fills the bottom sheet; requestFullscreen is the real Mini App
  // chrome (Bot API 8+). Safe-area CSS vars below keep content clear of TG's
  // close/more controls — do not skip fullscreen to "fix" padding.
  tg.expand();
  try {
    tg.requestFullscreen?.();
  } catch {
    /* older clients / platforms without fullscreen */
  }

  try {
    tg.setHeaderColor?.(tg.colorScheme === 'light' ? '#ffffff' : '#09090c');
    tg.setBackgroundColor?.(tg.colorScheme === 'light' ? '#f5f5f7' : '#09090c');
  } catch {
    /* older clients */
  }

  if (typeof tg.disableVerticalSwipes === 'function') {
    tg.disableVerticalSwipes();
  }

  syncTelegramSafeAreaInsets();
  if (typeof tg.onEvent === 'function') {
    tg.onEvent('safeAreaChanged', syncTelegramSafeAreaInsets);
    tg.onEvent('contentSafeAreaChanged', syncTelegramSafeAreaInsets);
    tg.onEvent('fullscreenChanged', () => {
      syncTelegramSafeAreaInsets();
      // Fullscreen toggles change viewport metrics — resync keyboard insets.
      bindTelegramViewportSync();
    });
    tg.onEvent('themeChanged', () => {
      try {
        tg.setHeaderColor?.(tg.colorScheme === 'light' ? '#ffffff' : '#09090c');
        tg.setBackgroundColor?.(tg.colorScheme === 'light' ? '#f5f5f7' : '#09090c');
      } catch {
        /* ignore */
      }
    });
  }

  // Prefer Telegram viewportChanged over visualViewport (avoids keyboard lag).
  bindTelegramViewportSync();
}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeRefreshToken(token: string | null) {
  try {
    if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function readStoredInitData(): string {
  try {
    return sessionStorage.getItem(TG_INITDATA_KEY) || '';
  } catch {
    return '';
  }
}

function persistInitData(initData: string): void {
  if (!initData) return;
  try {
    sessionStorage.setItem(TG_INITDATA_KEY, initData);
  } catch {
    /* private mode */
  }
}

function readInitDataFromLaunchUrl(): string {
  if (typeof window === 'undefined') return '';
  try {
    const fromSearch = new URLSearchParams(window.location.search).get('tgWebAppData');
    if (fromSearch) return fromSearch;
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash) return '';
    return new URLSearchParams(hash).get('tgWebAppData') || '';
  } catch {
    return '';
  }
}

export function getTelegramInitData(): string {
  if (typeof window === 'undefined') return '';

  const tg = window.Telegram?.WebApp;
  if (tg?.initData) {
    persistInitData(tg.initData);
    return tg.initData;
  }

  const stored = readStoredInitData();
  if (stored) return stored;

  const fromUrl = readInitDataFromLaunchUrl();
  if (fromUrl) {
    persistInitData(fromUrl);
    return fromUrl;
  }

  if (import.meta.env.DEV && import.meta.env.VITE_DEV_INIT_DATA) {
    return import.meta.env.VITE_DEV_INIT_DATA;
  }
  return '';
}
